/* OTERVANO — native Inbox launcher visibility and shortcuts only.
   No chat messages, customer data, checkout calls, new app or external request.
   Supports open shadow-root Inbox UI. Closed/cross-origin UI is not bypassed. */
(() => {
  'use strict';
  if (window.OtervanoInbox) return;
  const hostSelector = 'shopify-chat, #ShopifyChat';
  const buttonSelector = '.chat-app > button,button.activator,.activator[role="button"],.activator button,button.chat-toggle,button.shopify-chat__toggle,[data-testid="chat-toggle"],[data-testid="chat-activator"]';
  const dialogSelector = '[role="dialog"],.interstitial-view,.chat-view,.chat-window,.chat-app__window';
  const roots = new Map();
  let scanFrame = 0, pending = null, state = false, pendingOpen = false, opener = null;
  const shortcutsOnly = () => document.documentElement.dataset.ovInboxShortcutsOnly === 'true';
  const visible = el => {
    if (!el || el.hidden || !el.getClientRects().length) return false;
    const css = getComputedStyle(el);
    return css.display !== 'none' && css.visibility !== 'hidden' && css.opacity !== '0';
  };
  const isClose = el => /close|cerrar|fechar|fermer|schließen/i.test(`${el.getAttribute('aria-label') || ''} ${el.title || ''}`);
  const schedule = () => { if (!scanFrame) scanFrame = requestAnimationFrame(() => { scanFrame = 0; scan(); }); };
  function publish(open) {
    const next = Boolean(open || pendingOpen);
    document.documentElement.dataset.ovInboxOpen = String(next);
    if (state === next) return;
    const previous = state; state = next;
    document.dispatchEvent(new CustomEvent('otervano:inbox-state',{detail:{open:state}}));
    if (previous && !state && opener) {
      const target = visible(opener) ? opener : document.querySelector('#Details-menu-drawer-container > summary');
      if (visible(target)) target.focus({preventScroll:true});
      opener = null;
    }
  }
  function watch(root) {
    if (roots.has(root)) return;
    const style = document.createElement('style'); style.dataset.ovInboxStyle = '';
    style.textContent = '[data-ov-inbox-launcher="hidden"]{display:none!important;visibility:hidden!important;pointer-events:none!important;}';
    root.append(style);
    const observer = new MutationObserver(schedule);
    observer.observe(root,{subtree:true,childList:true,attributes:true,attributeFilter:['class','style','hidden','open','aria-expanded','aria-label','title']});
    roots.set(root,{observer,style});
  }
  function inspectRoots(host) {
    const found = [], visited = new Set();
    const visit = root => {
      if (!root || visited.has(root)) return;
      visited.add(root); found.push(root); watch(root);
      if (root.shadowRoot) visit(root.shadowRoot);
      root.querySelectorAll('*').forEach(el => { if (el.shadowRoot) visit(el.shadowRoot); });
    };
    visit(host); return found;
  }
  function scan() {
    const candidates = []; let open = false;
    const hosts = [...document.querySelectorAll(hostSelector)];
    for (const host of hosts) {
      const allRoots = inspectRoots(host);
      const buttons = [...new Set(allRoots.flatMap(root => [...root.querySelectorAll(buttonSelector)]))];
      // Mark recognized widgets ready after launcher controls are identified.
      // The stylesheet gate prevents a flash before the widget's shadow DOM is ready.
      if (buttons.length && !host.hasAttribute('data-ov-inbox-ready')) {
        // Hide launchers BEFORE releasing the outer gate (below).
        buttons.forEach(button => { if (shortcutsOnly() && !isClose(button) && button.getAttribute('aria-expanded') !== 'true') button.dataset.ovInboxLauncher = 'hidden'; });
        host.setAttribute('data-ov-inbox-ready','');
      }
      const dialogs = allRoots.flatMap(root => [...root.querySelectorAll(dialogSelector)]);
      const hostOpen = dialogs.some(visible) || buttons.some(button => button.getAttribute('aria-expanded') === 'true' || (isClose(button) && visible(button)));
      open ||= hostOpen;
      for (const button of buttons) {
        // A legacy toggle can change into the close control. Never hide that close control.
        const closeControl = isClose(button) || button.getAttribute('aria-expanded') === 'true';
        const hide = shortcutsOnly() && !closeControl;
        if (hide) { if (button.dataset.ovInboxLauncher !== 'hidden') button.dataset.ovInboxLauncher = 'hidden'; }
        else button.removeAttribute('data-ov-inbox-launcher');
        if (!closeControl && !button.disabled) candidates.push({button,host,open:hostOpen});
      }
      if (!shortcutsOnly()) host.setAttribute('data-ov-inbox-ready','');
    }
    // Avoid holding detached widget DOM after an app rerender.
    for (const [root,record] of roots) {
      if (!(root.host || root).isConnected) { record.observer.disconnect(); roots.delete(root); }
      else if (!record.style.isConnected) { root.append(record.style); }
    }
    publish(open); return {candidates,open};
  }
  function showError(message) {
    let status = document.getElementById('OtervanoInboxShortcutStatus');
    if (!status) {
      status = document.createElement('div'); status.id='OtervanoInboxShortcutStatus';
      status.className='ovm-inbox-status'; status.setAttribute('role','status'); document.body.append(status);
    }
    status.textContent = message || 'El chat no está disponible en este momento. Inténtalo de nuevo.';
    setTimeout(()=>{if(status.isConnected)status.textContent='';},6000);
  }
  async function open(message,trigger) {
    if (pending) return pending;
    pending = (async () => {
      opener = trigger || document.activeElement; pendingOpen = true; publish(state);
      document.dispatchEvent(new CustomEvent('otervano:inbox-shortcut-requested'));
      const start = performance.now(); let clicked = false;
      while (performance.now()-start < 8000) {
        const current = scan();
        if (current.open) { pendingOpen = false; publish(true); return true; }
        if (!clicked && current.candidates.length) {
          clicked = true;
          // Hidden native activators still accept a programmatic click.
          // No undocumented global API or duplicate widget is installed.
          current.candidates[0].button.click(); schedule();
        }
        await new Promise(resolve=>setTimeout(resolve,160));
      }
      pendingOpen = false; publish(scan().open); showError(message); return false;
    })();
    try { return await pending; } finally { pending = null; }
  }
  window.OtervanoInbox = {open,isOpen:()=>state,refresh:schedule};
  const docObserver = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches(hostSelector) || node.querySelector(hostSelector))))) schedule();
  });
  const start = () => {
    docObserver.observe(document.body,{childList:true,subtree:true}); schedule();
    ['shopify-chat','shopify-chat-ui','shopify-chat-agent'].forEach(name => customElements.whenDefined(name).then(schedule));
    // attachShadow itself does not emit a DOM mutation; bounded setup retries cover delayed loading.
    let attempts=0; const setupTimer=setInterval(()=>{schedule();if(++attempts>=20)clearInterval(setupTimer);},500);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});else start();
  document.addEventListener('shopify:section:load',schedule);
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)schedule();});
  document.addEventListener('click',event=>{if(event.composedPath().some(el=>el?.matches?.(hostSelector)))schedule();},true);
})();
